import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  QrCode,
  Copy,
  Check,
  RefreshCw,
  Smartphone,
  Shield,
  Trash2,
  ExternalLink,
  AlertTriangle,
  User,
  Clock,
  Globe,
  CheckCircle2
} from 'lucide-react';
import QRCode from 'qrcode';

export default function DriverQrConnectModal({
  driver,
  onClose,
  tenantId = 1,
  tenantName = 'Loops'
}) {
  const [loading, setLoading] = useState(true);
  const [pairingToken, setPairingToken] = useState('');
  const [resolvedTenant, setResolvedTenant] = useState({ id: tenantId, name: tenantName });
  const [devices, setDevices] = useState([]);
  const [copied, setCopied] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const canvasRef = useRef(null);

  // Compute pairing URL
  const serverOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://loops.thegreenfuture.ch';
  const pairingUrl = pairingToken
    ? `${serverOrigin}/driver-connect?server=${encodeURIComponent(serverOrigin)}&tenantId=${resolvedTenant.id}&driverId=${driver.id}&token=${pairingToken}`
    : '';

  const fetchPairingInfo = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await fetch(`/api/drivers/${driver.id}/pairing-info`);
      if (!res.ok) throw new Error('Impossible de charger les informations de jumelage');
      const data = await res.json();
      setPairingToken(data.pairing_token || '');
      if (data.tenant) {
        setResolvedTenant(data.tenant);
      }
      setDevices(data.devices || []);
    } catch (err) {
      console.error('Error fetching driver pairing info:', err);
      setErrorMsg(err.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (driver?.id) {
      fetchPairingInfo();
    }
  }, [driver?.id]);

  // Render QR Code onto Canvas
  useEffect(() => {
    if (!canvasRef.current || !pairingUrl) return;
    QRCode.toCanvas(
      canvasRef.current,
      pairingUrl,
      {
        width: 250,
        margin: 2,
        color: {
          dark: '#0f172a',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'M'
      },
      (err) => {
        if (err) console.error('Error rendering QR code:', err);
      }
    );
  }, [pairingUrl]);

  const handleCopyLink = () => {
    if (!pairingUrl) return;
    navigator.clipboard.writeText(pairingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('Voulez-vous vraiment régénérer le QR Code ? L\'ancien code ne fonctionnera plus pour de nouveaux appairages.')) {
      return;
    }
    setIsRegenerating(true);
    try {
      const res = await fetch(`/api/drivers/${driver.id}/pairing-token`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Échec de la régénération du jeton');
      const data = await res.json();
      setPairingToken(data.pairing_token);
      if (data.tenant) setResolvedTenant(data.tenant);
      alert('Nouveau QR Code généré avec succès !');
    } catch (err) {
      alert(err.message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleRevokeDevice = async (deviceId, devName) => {
    if (!window.confirm(`Voulez-vous révoquer l'accès pour "${devName || 'cet appareil'}" ? Le chauffeur sera déconnecté sur ce smartphone.`)) {
      return;
    }
    setRevokingId(deviceId);
    try {
      const res = await fetch(`/api/drivers/${driver.id}/devices/${deviceId}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Impossible de révoquer l\'appareil');
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch (err) {
      alert(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.75)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '20px'
    }}>
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '560px',
        maxHeight: '92vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid #334155',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#0f172a',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0f172a'
            }}>
              <QrCode size={24} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: '800', margin: 0, color: '#f8fafc' }}>
                Connexion Chauffeur & QR Appairage
              </h2>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '2px' }}>
                Organisation : <strong style={{ color: '#38bdf8' }}>{resolvedTenant.name}</strong> (Tenant #{resolvedTenant.id})
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Driver identity banner */}
          <div style={{
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#0284c7',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
                fontSize: '1rem'
              }}>
                {(driver?.first_name?.[0] || 'C')}{(driver?.last_name?.[0] || 'H')}
              </div>
              <div>
                <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc' }}>
                  {driver?.first_name} {driver?.last_name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {driver?.email} {driver?.phone ? `• ${driver.phone}` : ''}
                </div>
              </div>
            </div>

            <span style={{
              background: 'rgba(34, 197, 94, 0.15)',
              color: '#4ade80',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              padding: '3px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '800'
            }}>
              Permis {driver?.driving_license || 'B'}
            </span>
          </div>

          {/* QR Code Container */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            background: '#0f172a',
            borderRadius: '14px',
            border: '1px solid #334155'
          }}>
            {loading ? (
              <div style={{ padding: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#38bdf8' }}>
                <RefreshCw size={28} className="animate-spin" />
                <span style={{ fontSize: '0.85rem' }}>Génération du QR Code sécurisé...</span>
              </div>
            ) : errorMsg ? (
              <div style={{ padding: '30px', color: '#ef4444', textAlign: 'center' }}>
                <AlertTriangle size={32} style={{ margin: '0 auto 8px auto' }} />
                <p style={{ margin: 0 }}>{errorMsg}</p>
              </div>
            ) : (
              <>
                <div style={{
                  background: '#ffffff',
                  padding: '12px',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
                }}>
                  <canvas ref={canvasRef} style={{ display: 'block' }} />
                </div>

                <div style={{ textAlign: 'center', marginTop: '14px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#f8fafc' }}>
                    Scanner avec la caméra du smartphone
                  </div>
                  <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginTop: '3px', maxWidth: '360px' }}>
                    Le chauffeur est immédiatement authentifié et redirigé vers sa tournée du jour, sans aucun mot de passe à taper.
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Quick Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleCopyLink}
              disabled={!pairingUrl}
              style={{
                flex: 1,
                minWidth: '170px',
                background: copied ? '#10b981' : '#0284c7',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '0.82rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.15s ease'
              }}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? 'Lien copié dans le presse-papier !' : 'Copier le lien direct'}</span>
            </button>

            <a
              href={pairingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'rgba(56, 189, 248, 0.12)',
                color: '#38bdf8',
                border: '1px solid #38bdf8',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '0.82rem',
                fontWeight: '700',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <ExternalLink size={16} />
              <span>Ouvrir l'App</span>
            </a>

            <button
              type="button"
              onClick={handleRegenerateToken}
              disabled={isRegenerating}
              style={{
                background: '#334155',
                color: '#cbd5e1',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '0.82rem',
                fontWeight: '700',
                cursor: isRegenerating ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              title="Régénérer le token invalide les anciens QR codes"
            >
              <RefreshCw size={15} style={{ animation: isRegenerating ? 'spin 1s linear infinite' : 'none' }} />
              <span>Régénérer QR</span>
            </button>
          </div>

          {/* Connected Devices Section */}
          <div style={{
            borderTop: '1px solid #334155',
            paddingTop: '18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Smartphone size={16} color="#38bdf8" />
                <span style={{ fontSize: '0.86rem', fontWeight: '800', color: '#f8fafc' }}>
                  Appareils Smartphone Appairés ({devices.length})
                </span>
              </div>
              <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                Révocation à distance en cas de perte
              </span>
            </div>

            {devices.length === 0 ? (
              <div style={{
                background: '#0f172a',
                border: '1px dashed #334155',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                color: '#64748b',
                fontSize: '0.78rem'
              }}>
                Aucun smartphone n'est actuellement appairé avec ce compte chauffeur.
                Scannez le QR Code pour enregistrer un premier appareil.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {devices.map(dev => (
                  <div
                    key={dev.id}
                    style={{
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Smartphone size={18} color="#38bdf8" />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#f8fafc' }}>
                          {dev.device_name || 'Smartphone Chauffeur'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ textTransform: 'uppercase', color: '#38bdf8' }}>{dev.device_platform || 'mobile'}</span>
                          <span>•</span>
                          <span>Actif : {new Date(dev.last_active_at || dev.created_at).toLocaleString('fr-CH')}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRevokeDevice(dev.id, dev.device_name)}
                      disabled={revokingId === dev.id}
                      style={{
                        background: 'rgba(239, 68, 68, 0.12)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#f87171',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        cursor: revokingId === dev.id ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Révoquer cet appareil déconnectera immédiatement le smartphone"
                    >
                      <Trash2 size={13} />
                      <span>{revokingId === dev.id ? 'Révocation...' : 'Révoquer'}</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
